import { Upload01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "@/components/ui/toast";
import { api, ApiError, type ReviewResult } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { statusBadgeVariant } from "@/lib/case-format";
import { formatPhoneJsonPathLabel } from "@/lib/ready-gate-copy";
import {
  assetIdFromMediaUrl,
  collectReviewProblemImages,
  imagesForLocation,
  rewriteStringDeep,
  type ReviewProblemImage,
} from "@/lib/review-problem-images";
import { cn } from "@/lib/utils";
import { getAtPath, setAtPath } from "@/components/phone-json-editor/path-utils";

type ReplacedImage = {
  previewUrl: string;
  publicUrl: string;
};

export function ReviewReportCard({
  result,
  caseId,
  versionId,
  canReplace,
}: {
  result: ReviewResult;
  caseId: string;
  versionId: string;
  canReplace: boolean;
}) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [busyUrl, setBusyUrl] = useState<string | null>(null);
  const [replacedByUrl, setReplacedByUrl] = useState<
    Record<string, ReplacedImage>
  >({});
  const replacedByUrlRef = useRef(replacedByUrl);
  replacedByUrlRef.current = replacedByUrl;
  const [preview, setPreview] = useState<{
    src: string;
    label: string;
  } | null>(null);

  const assetsQuery = useQuery({
    queryKey: ["assets", versionId],
    queryFn: () => api.listAssets(versionId),
    enabled: auth.status === "authed" && Boolean(versionId),
  });

  const problems = useMemo(
    () =>
      collectReviewProblemImages({
        issues: result.issues,
        images: result.images,
      }),
    [result.issues, result.images],
  );

  const updatedAtByAssetId = useMemo(() => {
    const map = new Map<string, string>();
    for (const asset of assetsQuery.data?.assets ?? []) {
      map.set(asset.id, asset.updatedAt);
    }
    return map;
  }, [assetsQuery.data]);

  const srcForUrl = (url: string): string => {
    const replaced = replacedByUrl[url];
    if (replaced) return replaced.previewUrl;
    const assetId = assetIdFromMediaUrl(url);
    const updatedAt = assetId ? updatedAtByAssetId.get(assetId) : undefined;
    if (!updatedAt) return url;
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}v=${encodeURIComponent(updatedAt)}`;
  };

  useEffect(() => {
    return () => {
      for (const item of Object.values(replacedByUrlRef.current)) {
        if (item.previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(item.previewUrl);
        }
      }
    };
  }, []);

  const onReplace = async (image: ReviewProblemImage, file: File | null) => {
    if (!file || busyUrl || !canReplace) return;
    setBusyUrl(image.url);
    try {
      const publicUrl = await replaceReviewImage({
        versionId,
        image,
        file,
        knownAssetIds: new Set(
          (assetsQuery.data?.assets ?? []).map((asset) => asset.id),
        ),
      });
      const previewUrl = URL.createObjectURL(file);
      setReplacedByUrl((prev) => {
        const old = prev[image.url];
        if (old?.previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(old.previewUrl);
        }
        return { ...prev, [image.url]: { previewUrl, publicUrl } };
      });
      toast.add({
        type: "success",
        title: "图片已替换",
        description: file.name,
      });
      await queryClient.invalidateQueries({ queryKey: ["assets", versionId] });
      await queryClient.invalidateQueries({
        queryKey: ["phone-json", versionId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["media-readiness", versionId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["case-preview", caseId],
      });
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "replace_failed";
      const note =
        err instanceof ApiError && err.note ? `${code}: ${err.note}` : code;
      toast.add({
        type: "error",
        title: "替换失败",
        description: note,
        priority: "high",
      });
    } finally {
      setBusyUrl(null);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-medium">线索审阅 (review)</p>
        <Badge variant={result.ok ? "secondary" : "destructive"}>
          {result.ok ? "通过 / 有条件通过" : "发现问题"}
        </Badge>
        <span className="text-xs text-muted-foreground">
          图片 {result.imageCount}
          {result.imageFailures > 0
            ? `（${result.imageFailures} 提取失败）`
            : ""}
        </span>
      </div>
      <Alert variant={result.ok ? "default" : "destructive"}>
        <AlertTitle>
          {result.ok
            ? "审阅完成（不改 phone JSON）"
            : "审阅发现问题（不改 phone JSON）"}
        </AlertTitle>
        <AlertDescription>
          <p>{result.summary}</p>
          {result.model ? (
            <p className="mt-1 text-muted-foreground">模型：{result.model}</p>
          ) : null}
        </AlertDescription>
      </Alert>

      {problems.length > 0 ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-medium">问题图片</p>
            <p className="text-xs text-muted-foreground">
              审阅标出的图片。上传文件即可替换当前素材。
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {problems.map((image) => (
              <ProblemImageCard
                key={image.url}
                image={image}
                src={srcForUrl(image.url)}
                replaced={Boolean(replacedByUrl[image.url])}
                busy={busyUrl === image.url}
                canReplace={canReplace}
                onPreview={() =>
                  setPreview({
                    src: srcForUrl(image.url),
                    label: image.paths[0] ?? image.url,
                  })
                }
                onReplace={(file) => void onReplace(image, file)}
              />
            ))}
          </div>
        </div>
      ) : null}

      {result.issues.length > 0 ? (
        <div className="flex flex-col gap-2">
          {result.issues.map((issue, index) => {
            const matched = imagesForLocation(issue.location, result.images);
            return (
              <div
                key={`${issue.code}-${index}`}
                className={cn(
                  "rounded-md border px-3 py-2",
                  issue.severity === "error" && "border-destructive/40",
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={statusBadgeVariant(issue.severity)}>
                        {issue.severity === "error"
                          ? "错误"
                          : issue.severity === "warn"
                            ? "提醒"
                            : "信息"}
                      </Badge>
                      <span className="text-sm font-medium">{issue.title}</span>
                      {issue.location ? (
                        <span className="font-mono text-[0.65rem] text-muted-foreground">
                          {issue.location}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {issue.detail}
                    </p>
                    {issue.suggestion ? (
                      <p className="mt-1 text-xs">建议：{issue.suggestion}</p>
                    ) : null}
                  </div>
                  {matched.length > 0 ? (
                    <div className="flex shrink-0 gap-1">
                      {matched.slice(0, 3).map((img) => (
                        <button
                          key={`${img.path}:${img.url}`}
                          type="button"
                          className="overflow-hidden rounded-md border border-border"
                          onClick={() =>
                            setPreview({
                              src: srcForUrl(img.url),
                              label: img.path,
                            })
                          }
                        >
                          <img
                            src={srcForUrl(img.url)}
                            alt={img.path}
                            className="size-12 object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          无问题列表。运营仍可预览小手机后手改 JSON。
        </p>
      )}

      <Dialog
        open={preview != null}
        onOpenChange={(open) => {
          if (!open) setPreview(null);
        }}
      >
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>问题图片</DialogTitle>
            <DialogDescription>
              {preview
                ? formatPhoneJsonPathLabel(preview.label)
                : ""}
            </DialogDescription>
          </DialogHeader>
          {preview ? (
            <a href={preview.src} target="_blank" rel="noreferrer">
              <img
                src={preview.src}
                alt={preview.label}
                className="max-h-[70vh] w-full rounded-md object-contain"
              />
            </a>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProblemImageCard({
  image,
  src,
  replaced,
  busy,
  canReplace,
  onPreview,
  onReplace,
}: {
  image: ReviewProblemImage;
  src: string;
  replaced: boolean;
  busy: boolean;
  canReplace: boolean;
  onPreview: () => void;
  onReplace: (file: File | null) => void;
}) {
  const primaryPath = image.paths[0] ?? "";
  return (
    <div className="flex flex-col gap-2 rounded-md border border-border p-2">
      <button
        type="button"
        className="overflow-hidden rounded-md border border-border bg-muted/30"
        onClick={onPreview}
      >
        <img
          src={src}
          alt={primaryPath || image.url}
          className="max-h-56 w-full object-contain"
        />
      </button>
      <div className="flex flex-wrap items-center gap-1.5">
        {image.issues.some((issue) => issue.severity === "error") ? (
          <Badge variant="destructive">错误</Badge>
        ) : image.issues.some((issue) => issue.severity === "warn") ? (
          <Badge variant="info">提醒</Badge>
        ) : null}
        {!image.extractOk ? <Badge variant="warning">提取失败</Badge> : null}
        {replaced ? <Badge variant="success">已替换</Badge> : null}
      </div>
      {primaryPath ? (
        <div className="flex flex-col gap-0.5">
          <p className="text-xs font-medium">
            {formatPhoneJsonPathLabel(primaryPath)}
          </p>
          <p className="font-mono text-[0.65rem] text-muted-foreground">
            {image.paths.join(" · ")}
          </p>
        </div>
      ) : null}
      {image.issues[0] ? (
        <p className="text-xs">{image.issues[0].title}</p>
      ) : null}
      {image.description ? (
        <p className="line-clamp-3 text-xs text-muted-foreground">
          {image.description}
        </p>
      ) : null}
      {canReplace ? (
        <label className="inline-flex cursor-pointer items-center gap-2">
          <input
            type="file"
            accept="image/*"
            className="sr-only"
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              onReplace(f);
              e.target.value = "";
            }}
          />
          <Button
            variant="outline"
            disabled={busy}
            render={<span />}
            nativeButton={false}
          >
            {busy ? (
              <Spinner />
            ) : (
              <HugeiconsIcon icon={Upload01Icon} data-icon="inline-start" />
            )}
            替换
          </Button>
        </label>
      ) : null}
    </div>
  );
}

async function replaceReviewImage(opts: {
  versionId: string;
  image: ReviewProblemImage;
  file: File;
  knownAssetIds: Set<string>;
}): Promise<string> {
  const assetId = assetIdFromMediaUrl(opts.image.url);
  if (assetId && (opts.knownAssetIds.size === 0 || opts.knownAssetIds.has(assetId))) {
    try {
      const replaced = await api.replaceAsset(
        opts.versionId,
        assetId,
        opts.file,
      );
      return replaced.asset?.publicUrl ?? opts.image.url;
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 404) {
        throw err;
      }
    }
  }

  const uploaded = await api.uploadAsset(opts.versionId, opts.file, {
    fieldKey: opts.image.paths[0],
  });
  const newUrl = uploaded.asset?.publicUrl;
  if (!newUrl) {
    throw new ApiError(500, "upload_no_url");
  }

  const phone = await api.getPhoneJson(opts.versionId);
  if (phone.phoneJson != null) {
    let next = rewriteStringDeep(phone.phoneJson, opts.image.url, newUrl);
    for (const path of opts.image.paths) {
      const current = getAtPath(next, path);
      if (typeof current === "string") {
        next = setAtPath(next, path, newUrl);
      }
    }
    await api.savePhoneJson(opts.versionId, { phoneJson: next });
  }
  return newUrl;
}
